import { createAction, createSlice } from "@reduxjs/toolkit";
import { uniq } from "lodash";

import type {
  AppStateVar6,
  AppTimer6,
  EventDefinition,
} from "@superblocksteam/shared";

import type { WidgetMap } from "legacy/widgets";

import type { ApiDtoWithPb } from "../apisV2/slice";
import {
  getCurrentSdlcMessages,
  getLastSdlcMessage,
  incrementActionCountOnLastImplementSdlcMessage,
} from "./sdlcUtils";
import { ClarkFocusedEntityType, RESPONSE_SCHEMA_TYPE } from "./types";
import type {
  EditMetadata,
  DiscardedEdit,
  ResultHistoryItem,
  ClarkAction,
  SldcMessages,
  SldcMessage,
  ClarkWebSocketConnectionClosedReason,
} from "./types";

export const promptHistoryId = (entityId: string, entityType: string) => {
  return `${entityType}__${entityId}`;
};

const initialState: {
  isLoading: boolean;
  focusedEntityId?: string;
  focusedEntityType?: ClarkFocusedEntityType;
  viewMode: "chat" | "modal";
  initialPosition?: { x: number; y: number };
  actions?: Array<ClarkAction>;
  propertiesToChange?: Record<string, string[]>;
  error?: string;
  actionsRequestId?: string;
  discardedEdits?: Record<string, DiscardedEdit[]>;
  noResultsResponse?: {
    summary: string;
    suggestion?: string;
  };
  metadataByWidgetId?: Record<string, EditMetadata | undefined>;
  promptHistoryByEntityTypeAndId?: Record<string, string[]>;
  resultsHistory: Array<ResultHistoryItem>;
  sdlcMessagesHistory: Array<SldcMessages>;
  apiStatuses?: Record<string, "STARTED" | "COMPLETED">;
  connectionClosedReason?: ClarkWebSocketConnectionClosedReason;
  prompt: string;

  // pre-ai data
  widgetsAtRequestStart?: WidgetMap;
  apisAtRequestStart?: Record<string, ApiDtoWithPb>;
  widgetsUndoStackLengthAtRequestStart?: number;
  apisUndoStackLengthsAtRequestStart?: Record<string, number>;
  stateVariablesAtRequestStart?: Record<string, AppStateVar6>;
  timersAtRequestStart?: Record<string, AppTimer6>;
  eventsAtRequestStart?: Record<string, EventDefinition>;

  // changes and changed keys
  widgetChangedKeys?: Record<string, string[]>;
  widgetChanges?: Record<string, Record<string, unknown> | null>;
  widgetMovesById?: Record<
    string,
    {
      position: "left" | "right" | "above" | "below" | "inside";
      relativeTo: string;
    }
  >;
  apiChanges?: Record<string, ApiDtoWithPb>;
  changedApiBlockPaths?: Record<string, string[]>;
  stateVariableChanges?: Record<string, Record<string, unknown> | null>;
  stateVariableChangedKeys?: Record<string, string[]>;
  timerChanges?: Record<string, Record<string, unknown> | null>;
  timerChangedKeys?: Record<string, string[]>;
  eventChanges?: Record<string, Record<string, unknown> | null>;
  eventChangedKeys?: Record<string, string[]>;
  originalNamesById?: Record<string, string>;
  renamesById?: Record<string, string>;
} = {
  isLoading: false,
  viewMode: "chat",
  resultsHistory: [],
  sdlcMessagesHistory: [[]],
  prompt: "",
};

type NoResultsResponse = {
  summary: string;
  suggestion?: string;
};

export const widgetCreated = createAction<{
  widgetId: string;
  widgetName: string;
  widgetType: string;
}>("ai/widgetCreated");

export const widgetDeleted = createAction<{
  widgetIds: string[];
}>("ai/widgetDeleted");

export const processAiActions = createAction<{
  clarkRequestId: string;
  actions: ClarkAction[];
  existingEntityId: string;
  existingEntityType: string;
  existingComponentType: string;
}>("ai/processAiActions");

export const removeChildInAiChanges = createAction<{
  widgetId: string;
}>("ai/removeChildInAiChanges");

export const restoreDeletedChildInAiChanges = createAction<{
  childId: string;
}>("ai/restoreDeletedChildInAiChanges");

export const processAiProperties = createAction<
  | {
      componentName: string;
      properties: string[];
    }
  | {
      noResultsResponse?: NoResultsResponse;
    }
>("ai/processAiProperties");

export const clearAiChanges = createAction<{
  isAccept?: boolean;
  shouldClose: boolean;
  isRedo?: boolean;
}>("ai/clearAiChanges");

export const doSetupBeforeClarkRequest = createAction<{
  isRedo?: boolean;
}>("ai/doSetupBeforeClarkRequest");

export const aiSlice = createSlice({
  name: "ai",
  initialState,
  reducers: {
    setFocusedEntity: (
      state,
      action: {
        payload: { entityId?: string; entityType?: ClarkFocusedEntityType };
      },
    ) => {
      state.focusedEntityId = action.payload.entityId;
      state.focusedEntityType = action.payload.entityType;
      state.viewMode = "chat";
    },
    resetAiState: (
      state,
      action: { payload: { shouldClose: boolean; isRedo?: boolean } },
    ) => {
      state.actionsRequestId = undefined;

      state.isLoading = action.payload.isRedo ? true : false;
      state.actions = undefined;
      state.propertiesToChange = undefined;
      state.error = undefined;
      state.discardedEdits = undefined;
      state.metadataByWidgetId = undefined;
      state.noResultsResponse = undefined;
      state.apiStatuses = undefined;
      state.connectionClosedReason = undefined;
      // clear out changes and changed keys
      state.renamesById = undefined;
      state.widgetChangedKeys = undefined;
      state.widgetChanges = undefined;
      state.widgetMovesById = undefined;
      state.apiChanges = undefined;
      state.changedApiBlockPaths = undefined;
      state.stateVariableChanges = undefined;
      state.stateVariableChangedKeys = undefined;
      state.timerChanges = undefined;
      state.timerChangedKeys = undefined;
      state.eventChanges = undefined;
      state.eventChangedKeys = undefined;
      state.originalNamesById = undefined;
      if (!action.payload.isRedo) {
        state.widgetsUndoStackLengthAtRequestStart = undefined;
        state.apisUndoStackLengthsAtRequestStart = undefined;
        state.widgetsAtRequestStart = undefined;
        state.apisAtRequestStart = undefined;
        state.stateVariablesAtRequestStart = undefined;
        state.timersAtRequestStart = undefined;
        state.eventsAtRequestStart = undefined;
      }

      if (action.payload.shouldClose) {
        state.focusedEntityId = undefined;
        state.focusedEntityType = undefined;
        state.initialPosition = undefined;
        state.viewMode = "chat";
      }
    },
    setAiChanges: (
      state,
      action: {
        payload: {
          widgetChangedKeys?: Record<string, string[]>;
          widgetChanges?: Record<string, Record<string, unknown> | null>;
          widgetMovesById?: Record<
            string,
            {
              position: "left" | "right" | "above" | "below" | "inside";
              relativeTo: string;
            }
          >;
          renamesById?: Record<string, string>;
          discardedEdits?: Record<string, DiscardedEdit[]>;
          metadataByWidgetId?: Record<string, EditMetadata | undefined>;
          apiChanges?: Record<string, ApiDtoWithPb>;
          changedApiBlockPaths?: Record<string, string[]>;
          apiStatuses?: Record<string, "STARTED" | "COMPLETED">;
          stateVariableChangedKeys?: Record<string, string[]>;
          stateVariableChanges?: Record<string, Record<string, unknown> | null>;
          timerChangedKeys?: Record<string, string[]>;
          timerChanges?: Record<string, Record<string, unknown> | null>;
          eventChangedKeys?: Record<string, string[]>;
          eventChanges?: Record<string, Record<string, unknown> | null>;
          originalNamesById?: Record<string, string>;
        };
      },
    ) => {
      const {
        widgetChangedKeys,
        widgetChanges,
        widgetMovesById,
        renamesById,
        discardedEdits,
        metadataByWidgetId,
        apiChanges,
        changedApiBlockPaths,
        apiStatuses,
        stateVariableChangedKeys,
        stateVariableChanges,
        timerChangedKeys,
        timerChanges,
        eventChangedKeys,
        eventChanges,
        originalNamesById,
      } = action.payload;
      state.widgetChangedKeys = widgetChangedKeys;
      state.widgetMovesById = widgetMovesById;
      state.widgetChanges = widgetChanges;
      state.renamesById = renamesById;
      state.discardedEdits = discardedEdits;
      state.metadataByWidgetId = metadataByWidgetId ?? state.metadataByWidgetId;
      state.apiChanges = apiChanges;
      state.changedApiBlockPaths = changedApiBlockPaths;
      state.apiStatuses = apiStatuses ?? state.apiStatuses;
      state.stateVariableChangedKeys = stateVariableChangedKeys;
      state.stateVariableChanges = stateVariableChanges;
      state.timerChangedKeys = timerChangedKeys;
      state.timerChanges = timerChanges;
      state.eventChangedKeys = eventChangedKeys;
      state.eventChanges = eventChanges;
      state.originalNamesById = originalNamesById;
    },
    openAiModal: (
      state,
      action: {
        payload: { widgetId: string; position: { x: number; y: number } };
      },
    ) => {
      state.focusedEntityId = action.payload.widgetId;
      state.focusedEntityType = ClarkFocusedEntityType.WIDGET;
      state.initialPosition = action.payload.position;
      state.viewMode = "modal";
      // clear all the previous change
      state.actionsRequestId = undefined;
      state.isLoading = false;
      state.actions = undefined;
      state.propertiesToChange = undefined;
      state.error = undefined;
      state.actionsRequestId = undefined;
      state.discardedEdits = undefined;
      state.metadataByWidgetId = undefined;
      state.noResultsResponse = undefined;
      state.apiStatuses = undefined;
      // clear out changes and changed keys
      state.renamesById = undefined;
      state.widgetChangedKeys = undefined;
      state.widgetChanges = undefined;
      state.widgetMovesById = undefined;
      state.apiChanges = undefined;
      state.changedApiBlockPaths = undefined;
      state.stateVariableChanges = undefined;
      state.stateVariableChangedKeys = undefined;
      state.timerChanges = undefined;
      state.timerChangedKeys = undefined;
      state.eventChanges = undefined;
      state.eventChangedKeys = undefined;
      state.originalNamesById = undefined;
    },
    setIsLoading: (state, action: { payload: boolean }) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = undefined;
      }
    },
    setError: (state, action: { payload: { error: string } }) => {
      state.error = action.payload.error;
    },
    setPropertiesToChange: (
      state,
      action: { payload: { widgetId: string; properties: string[] } },
    ) => {
      state.propertiesToChange = {
        ...state.propertiesToChange,
        [action.payload.widgetId]: uniq([
          ...(state.propertiesToChange?.[action.payload.widgetId] ?? []),
          ...action.payload.properties,
        ]),
      };
    },
    setNoResultsResponse: (state, action: { payload: NoResultsResponse }) => {
      state.noResultsResponse = action.payload;
    },
    addAction: (state, action: { payload: ClarkAction }) => {
      state.actions = [...(state.actions ?? []), action.payload];

      incrementActionCountOnLastImplementSdlcMessage(state.sdlcMessagesHistory);
    },
    updateSdlcActiveTest: (
      state,
      action: {
        payload: Omit<
          Extract<SldcMessage, { type: RESPONSE_SCHEMA_TYPE.SDLC_TEST_EVENT }>,
          "type" | "message"
        >;
      },
    ) => {
      const lastMessage = getLastSdlcMessage(state.sdlcMessagesHistory);
      const isTest =
        lastMessage &&
        lastMessage.type === RESPONSE_SCHEMA_TYPE.SDLC_TEST_EVENT &&
        lastMessage.id === action.payload.id;

      if (!isTest) {
        throw new Error("Clark: No active or matching SDLC test to update");
      }

      lastMessage.status = action.payload.status;
      lastMessage.answer = action.payload.answer;
    },

    initSdlcMessagesForNextPrompt: (state) => {
      state.sdlcMessagesHistory.push([]);
    },
    pushSdlcMessage: (
      state,
      action: {
        payload: SldcMessage;
      },
    ) => {
      const lastSdlcMessages = getCurrentSdlcMessages(
        state.sdlcMessagesHistory,
      );
      if (!lastSdlcMessages) {
        throw new Error("Clark: No active SDLC messages structure");
      }
      lastSdlcMessages.push(action.payload);
    },
    setActionsRequestId: (state, action: { payload: string | undefined }) => {
      state.actionsRequestId = action.payload;
    },
    setApiStatus: (
      state,
      action: { payload: { apiName: string; status: "STARTED" | "COMPLETED" } },
    ) => {
      state.apiStatuses = {
        ...(state.apiStatuses ?? {}),
        [action.payload.apiName]: action.payload.status,
      };
    },
    updatePromptHistoryByEntityTypeAndId: (
      state,
      action: {
        payload: { entityId: string; entityType: string; prompt: string };
      },
    ) => {
      const id = promptHistoryId(
        action.payload.entityId,
        action.payload.entityType,
      );
      state.promptHistoryByEntityTypeAndId = {
        ...state.promptHistoryByEntityTypeAndId,
        [id]: [
          action.payload.prompt,
          ...(state.promptHistoryByEntityTypeAndId?.[id] ?? []),
        ],
      };
    },
    addHistoryItem: (state, action: { payload: ResultHistoryItem }) => {
      state.resultsHistory = [...state.resultsHistory, action.payload];
    },
    setUndoStackIndexAtRequestStart: (
      state,
      action: {
        payload: {
          widgetsUndoStackLength: number;
          apisUndoStackLengths: Record<string, number>;
        };
      },
    ) => {
      state.widgetsUndoStackLengthAtRequestStart =
        action.payload.widgetsUndoStackLength;
      state.apisUndoStackLengthsAtRequestStart =
        action.payload.apisUndoStackLengths;
    },
    setStateAtRequestStart: (
      state,
      action: {
        payload: {
          widgetsAtRequestStart: WidgetMap | undefined;
          apisAtRequestStart: Record<string, ApiDtoWithPb> | undefined;
          stateVariablesAtRequestStart:
            | Record<string, AppStateVar6>
            | undefined;
          timersAtRequestStart: Record<string, AppTimer6> | undefined;
          eventsAtRequestStart: Record<string, EventDefinition> | undefined;
        };
      },
    ) => {
      state.widgetsAtRequestStart = action.payload.widgetsAtRequestStart;
      state.apisAtRequestStart = action.payload.apisAtRequestStart;
      state.stateVariablesAtRequestStart =
        action.payload.stateVariablesAtRequestStart;
      state.timersAtRequestStart = action.payload.timersAtRequestStart;
      state.eventsAtRequestStart = action.payload.eventsAtRequestStart;
    },
    setConnectionClosedReason: (
      state,
      action: {
        payload: ClarkWebSocketConnectionClosedReason | undefined;
      },
    ) => {
      state.connectionClosedReason = action.payload;
    },
    setPrompt: (state, action: { payload: string }) => {
      state.prompt = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase("FETCH_APPLICATION_INIT", (state) => {
      return initialState;
    });

    // When a draft change is in progress, we want to add additional changes made by the user

    // handle widget changes (update, delete, create)
    builder.addCase(widgetCreated.type, (state, action: any) => {
      if (state.actionsRequestId && !state.isLoading) {
        const { widgetId, widgetName, widgetType } = action.payload;
        state.widgetChanges = {
          ...state.widgetChanges,
          [widgetId]: {
            widgetName,
            type: widgetType,
            widgetId,
          },
        };
        state.widgetChangedKeys = {
          ...state.widgetChangedKeys,
          [widgetId]: ["widgetId", "widgetName", "type"],
        };
        state.renamesById = {
          ...state.renamesById,
          [widgetId]: widgetName ?? "",
        };
      }
    });

    builder.addCase(widgetDeleted.type, (state, action: any) => {
      if (state.actionsRequestId && !state.isLoading) {
        const { widgetIds } = action.payload;
        if (!widgetIds) {
          return;
        }
        const deletedWidgetObject = Object.fromEntries(
          widgetIds.map((widgetId: string) => [widgetId, null]),
        );
        state.widgetChanges = {
          ...state.widgetChanges,
          ...deletedWidgetObject,
        };
        const deletedWidgetKeys = Object.fromEntries(
          widgetIds.map((widgetId: string) => [widgetId, []]),
        );
        state.widgetChangedKeys = {
          ...state.widgetChangedKeys,
          ...deletedWidgetKeys,
        };
      }
    });

    builder.addCase(
      "renameApplicationEntitySaga/success",
      (state, action: any) => {
        if (state.actionsRequestId && !state.isLoading) {
          const { entityId, newName } = action.meta.args;
          state.renamesById = {
            ...state.renamesById,
            [entityId]: newName,
          };
        }
      },
    );

    builder.addCase("UPDATE_WIDGET_PROPERTIES", (state, action: any) => {
      if (state.actionsRequestId && !state.isLoading) {
        const { widgetId, updates } = action.payload;
        state.widgetChanges = {
          ...state.widgetChanges,
          [widgetId]: {
            ...state.widgetChanges?.[widgetId],
            ...updates,
          },
        };
        state.widgetChangedKeys = {
          ...state.widgetChangedKeys,
          [widgetId]: [
            ...(state.widgetChangedKeys?.[widgetId] ?? []),
            ...Object.keys(updates),
          ],
        };
      }
    });

    // handle api changes (update, delete, create)
    // todo: don't hardcode these as strings (circular deps issues)
    builder.addCase("apisV2->createV2ApiSaga/success", (state, action: any) => {
      if (state.actionsRequestId && !state.isLoading) {
        const {
          payload: {
            result: { apiPb, id, name },
          },
        } = action;
        state.apiChanges = {
          ...state.apiChanges,
          [id]: {
            apiPb,
            name: name,
          },
        };
      }
    });

    // todo (eric): handle editing apis

    builder.addCase("apisV2->deleteV2ApiSaga/success", (state, action: any) => {
      if (state.actionsRequestId && !state.isLoading) {
        const {
          payload: { id },
        } = action;
        state.apiChanges = {
          ...state.apiChanges,
          [id]: null,
        };
      }
    });

    // handle state variable changes (update, delete, create)
    builder.addCase("CREATE_STATE_VAR", (state, action: any) => {
      if (state.actionsRequestId && !state.isLoading) {
        const { id } = action.payload;
        if (!id) return;
        state.stateVariableChanges = {
          ...state.stateVariableChanges,
          [id]: {},
        };
        state.stateVariableChangedKeys = {
          ...state.stateVariableChangedKeys,
          [id]: [],
        };
      }
    });

    builder.addCase("UPDATE_STATE_VAR", (state, action: any) => {
      if (state.actionsRequestId && !state.isLoading) {
        const { stateVars } = action.payload;
        const mergedStateVars = Object.keys(stateVars).reduce((acc, id) => {
          return {
            ...acc,
            [id]: {
              ...state.stateVariableChanges?.[id],
              ...stateVars[id],
            },
          };
        }, {});
        state.stateVariableChanges = {
          ...state.stateVariableChanges,
          ...mergedStateVars,
        };
        const mergedStateVarChangedKeys = Object.keys(stateVars).reduce(
          (acc, id) => {
            return {
              ...acc,
              [id]: [
                ...(state.stateVariableChangedKeys?.[id] ?? []),
                ...Object.keys(stateVars[id]),
              ],
            };
          },
          {},
        );
        state.stateVariableChangedKeys = {
          ...state.stateVariableChangedKeys,
          ...mergedStateVarChangedKeys,
        };
      }
    });

    builder.addCase("DELETE_STATE_VAR", (state, action: any) => {
      if (state.actionsRequestId && !state.isLoading) {
        const { id } = action.payload;
        if (!id) return;
        state.stateVariableChanges = {
          ...state.stateVariableChanges,
          [id]: null,
        };
        state.stateVariableChangedKeys = {
          ...state.stateVariableChangedKeys,
          [id]: [],
        };
      }
    });

    // handle timer changes (update, delete, create)
    builder.addCase("CREATE_TIMER", (state, action: any) => {
      if (state.actionsRequestId && !state.isLoading) {
        const { id } = action.payload;
        if (!id) return;
        state.timerChanges = {
          ...state.timerChanges,
          [id]: {},
        };
        state.timerChangedKeys = {
          ...state.timerChangedKeys,
          [id]: [],
        };
      }
    });

    builder.addCase("UPDATE_TIMERS", (state, action: any) => {
      if (state.actionsRequestId && !state.isLoading) {
        const timers = action.payload;
        const mergedTimers = Object.keys(timers).reduce((acc, id) => {
          return {
            ...acc,
            [id]: {
              ...state.timerChanges?.[id],
              ...timers[id].updates,
            },
          };
        }, {});
        state.timerChanges = {
          ...state.timerChanges,
          ...mergedTimers,
        };
        const mergedTimerChangedKeys = Object.keys(timers).reduce((acc, id) => {
          return {
            ...acc,
            [id]: [
              ...(state.timerChangedKeys?.[id] ?? []),
              ...Object.keys(timers[id].updates),
            ],
          };
        }, {});
        state.timerChangedKeys = {
          ...state.timerChangedKeys,
          ...mergedTimerChangedKeys,
        };
      }
    });

    builder.addCase("DELETE_TIMER", (state, action: any) => {
      if (state.actionsRequestId && !state.isLoading) {
        const { id } = action.payload;
        if (!id) return;
        state.timerChanges = {
          ...state.timerChanges,
          [id]: null,
        };
        state.timerChangedKeys = {
          ...state.timerChangedKeys,
          [id]: [],
        };
      }
    });

    // handle event changes (update, delete, create)
    builder.addCase("CREATE_CUSTOM_EVENT", (state, action: any) => {
      if (state.actionsRequestId && !state.isLoading) {
        const { id } = action.payload;
        if (!id) return;
        state.eventChanges = {
          ...state.eventChanges,
          [id]: {},
        };
        state.eventChangedKeys = {
          ...state.eventChangedKeys,
          [id]: [],
        };
      }
    });

    builder.addCase("UPDATE_CUSTOM_EVENTS", (state, action: any) => {
      if (state.actionsRequestId && !state.isLoading) {
        const { updates: events } = action.payload;
        const mergedEvents = Object.keys(events).reduce((acc, id) => {
          return {
            ...acc,
            [id]: {
              ...state.eventChanges?.[id],
              ...events[id],
            },
          };
        }, {});
        state.eventChanges = {
          ...state.eventChanges,
          ...mergedEvents,
        };
        const mergedEventChangedKeys = Object.keys(events).reduce((acc, id) => {
          return {
            ...acc,
            [id]: [
              ...(state.eventChangedKeys?.[id] ?? []),
              ...Object.keys(events[id]),
            ],
          };
        }, {});
        state.eventChangedKeys = {
          ...state.eventChangedKeys,
          ...mergedEventChangedKeys,
        };
      }
    });

    builder.addCase("DELETE_CUSTOM_EVENT", (state, action: any) => {
      if (state.actionsRequestId && !state.isLoading) {
        const { id } = action.payload;
        if (!id) return;
        state.eventChanges = {
          ...state.eventChanges,
          [id]: null,
        };
        state.eventChangedKeys = {
          ...state.eventChangedKeys,
          [id]: [],
        };
      }
    });
  },
});

export const {
  resetAiState,
  setFocusedEntity,
  setAiChanges,
  setPropertiesToChange,
  setNoResultsResponse,
  setUndoStackIndexAtRequestStart,
  setStateAtRequestStart,
  setApiStatus,
} = aiSlice.actions;

export const AI_ACTIONS_FOR_IFRAME = [
  setAiChanges.type,
  clearAiChanges.type,
  removeChildInAiChanges.type,
  resetAiState.type,
  restoreDeletedChildInAiChanges.type,
];

export const AI_ACTIONS_CAUSING_EVALUATION = [
  setAiChanges.type,
  clearAiChanges.type,
  resetAiState.type,
  removeChildInAiChanges.type,
];
